import { useState, useCallback, useMemo } from 'react';

import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import {
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControl,
  FormHelperText,
  Autocomplete,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { debounce, isEmpty } from 'lodash';
import {
  useNotify,
  useRefresh,
  useUpdate,
  useGetList,
  useRecordContext,
  Button as ActionButton,
  useDataProvider,
  HttpError,
} from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { OfferDto } from 'src/dtos';
import { HttpErrorTypography } from 'src/lib';
import { UserDetails } from 'src/models';
import * as yup from 'yup';

interface PostcloseInvestFormValues {
  offerIds: string[];
}

const validationSchema = yup.object().shape({
  offerIds: yup.array().required('Postclose Offer is required'),
});

export const PostcloseOfferInvestUserActionButton = () => {
  const user = useRecordContext<UserDetails>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading }] = useUpdate();

  const [open, setOpen] = useState(false);
  const dataProvider = useDataProvider();
  const [searchedOffers, setSearchedOffers] = useState<OfferDto[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [offerError, setOfferError] = useState<HttpError | null>(null);

  const { data: defaultOffers = [], isLoading: defaultOffersLoading } = useGetList<OfferDto>(
    'offers',
    {
      filter: {
        ids: user?.postcloseOffers || [],
      },
      meta: { skipPagination: true },
    },
    {
      enabled: !isEmpty(user.postcloseOffers),
    },
  );

  const offerOptions = useMemo(() => {
    const optionsMap = new Map<string, OfferDto>();

    // Always include default offers (selected items), Material UI warns if the selected value is not in the options list
    defaultOffers.forEach(offer => {
      optionsMap.set(offer.id, offer);
    });

    // Add search results
    searchedOffers.forEach(offer => {
      optionsMap.set(offer.id, offer);
    });

    return Array.from(optionsMap.values());
  }, [searchedOffers, defaultOffers]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      setOffersLoading(true);
      dataProvider
        .getList<OfferDto>('offers', {
          pagination: { page: 1, perPage: 25 },
          sort: { field: 'name', order: 'ASC' },
          filter: {
            OR: { status: 'Closed', visibleToEndUsers: false },
            name: searchValue,
          },
          meta: { skipPagination: true },
        })
        .then(response => {
          setSearchedOffers(response.data);
        })
        .catch(error => {
          setOfferError(error);
        })
        .finally(() => {
          setOffersLoading(false);
        });
    }, 300),
    [dataProvider],
  );

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (values: PostcloseInvestFormValues) => {
    update(
      DataProviderResource.UpdateUserPostcloseOffers,
      { id: user?.id, data: { offerIds: values.offerIds } },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          notify(`Postclose offer investment successfully updated.`, {
            type: 'success',
            undoable: true,
          });
          handleClose();
        },
        onSettled: () => {
          refresh();
        },
      },
    );
  };

  const formik = useFormik<PostcloseInvestFormValues>({
    initialValues: {
      offerIds: defaultOffers?.map(offer => offer.id) ?? [],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit,
  });

  if (defaultOffersLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      <ActionButton
        label='Postclose Invest'
        onClick={() => setOpen(true)}
        disabled={isLoading}
        style={{ marginTop: '8px' }}>
        <AddShoppingCartIcon />
      </ActionButton>
      <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
          <DialogTitle>Postclose Offer Investment</DialogTitle>
          <DialogContent>
            <Grid container direction='column' spacing={2}>
              <Grid item xs={6} md={6}>
                <FormControl component='fieldset' fullWidth error={formik.touched.offerIds && !!formik.errors.offerIds}>
                  <Autocomplete
                    id='postclose-offers-autocomplete'
                    multiple
                    defaultValue={defaultOffers}
                    options={offerOptions}
                    getOptionLabel={option => option.name}
                    getOptionKey={option => option.id}
                    onChange={(_, newValue) => {
                      formik.setFieldValue(
                        'offerIds',
                        newValue.map(option => option.id),
                      );
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === 'input') {
                        debouncedSearch(newInputValue);
                      }
                    }}
                    loading={offersLoading}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    filterOptions={x => x}
                    noOptionsText={offersLoading ? 'Loading...' : 'No offers found'}
                    renderInput={params => {
                      return <TextField {...params} variant='standard' label='Offer Name' placeholder='Search' />;
                    }}
                  />
                  {formik.touched.offerIds && formik.errors.offerIds && (
                    <FormHelperText>{formik.errors.offerIds as unknown as string}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid container direction='column' spacing={2}>
                <Grid item md={12} sm={12}>
                  {offerError && <HttpErrorTypography error={offerError} />}
                </Grid>
              </Grid>
              <Grid item xs={6} md={6}>
                <Typography variant='subtitle2' color='textSecondary'>
                  *The search result will include only closed or non-visible to user offers.
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Grid item xs={12} container direction='row' justifyContent='flex-end' spacing={2}>
              <Grid item>
                <Button variant='outlined' color='inherit' size='medium' onClick={handleClose}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button variant='contained' color='primary' size='medium' disabled={isLoading} type='submit'>
                  Save
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
